import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Clock, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { createSafeRichHTML } from '../../lib/sanitize';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VersionHistoryModal({ pageId, onClose, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);

  useEffect(() => {
    fetchVersions();
  }, [pageId]);

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/pages/${pageId}/versions`, {
        withCredentials: true,
      });
      setVersions(res.data.versions || []);
    } catch (error) {
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (version) => {
    if (!window.confirm(`Restore to version ${version.version}? This will create a new version with the restored content.`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/api/admin/pages/${pageId}/restore/${version.version}`,
        {},
        { withCredentials: true }
      );
      toast.success('Version restored successfully');
      onRestore();
      onClose();
    } catch (error) {
      toast.error('Failed to restore version');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-lg w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <Clock size={24} weight="duotone" />
            Version History
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="text-muted-foreground mt-4">Loading versions...</p>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Clock size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
              <p>No previous versions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {versions.map((version, index) => (
                <div
                  key={version.version}
                  className={`border border-border rounded-lg p-4 ${
                    index === 0 ? 'bg-green-50 border-green-200' : 'hover:bg-muted/30'
                  } cursor-pointer`}
                  onClick={() => setSelectedVersion(selectedVersion?.version === version.version ? null : version)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-semibold">
                          Version {version.version}
                        </span>
                        {index === 0 && (
                          <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">
                            Current
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {new Date(version.created_at).toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                        </span>
                      </div>
                      
                      {selectedVersion?.version === version.version && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-sm font-semibold mb-2">Title:</p>
                          <p className="text-sm mb-3">{version.title}</p>
                          
                          <p className="text-sm font-semibold mb-2">Content Preview:</p>
                          <div
                            className="text-sm text-muted-foreground prose prose-sm max-w-none bg-muted/30 p-3 rounded max-h-40 overflow-y-auto"
                            dangerouslySetInnerHTML={createSafeRichHTML(version.content?.substring(0, 500) + '...')}
                          />
                        </div>
                      )}
                    </div>

                    {index !== 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRestore(version);
                        }}
                        className="ml-4 inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-primary/90"
                      >
                        <ArrowCounterClockwise size={16} weight="bold" />
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
